import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  HttpClientError,
  IQuerySettings,
  QueryKeys,
  getQuerySettings,
} from "@dw/models/shared";
import { useHttpClient, useSnackbar } from "@dw/hooks";
import {
  ICreateDanceSchoolRequest,
  IDanceSchool,
  IUpdateDanceSchoolBillingSettingsRequest,
  IUpdateDanceSchoolPublicInfoRequest,
  IUpdateDanceSchoolTermsAndConditionsRequest,
  IUpdateDanceSchoolXeroSalesAccountCodeRequest,
  IUpsertDanceSchoolAnnualSettingsRequest,
} from "@dw/models/danceSchool";
import { IStartDanceSchoolBillingYearRequest } from "@dw/models/dancerAccount";

const BASE_URL = "/v1/dance-schools";

enum DanceSchoolQueryKeys {
  DanceSchoolList = "DanceSchoolList",
}

export const useGetMyDanceSchools = (
  settings?: IQuerySettings<IDanceSchool[]>
) => {
  const { GET } = useHttpClient();

  const querySettings = getQuerySettings(settings);
  return useQuery<IDanceSchool[], HttpClientError>({
    queryKey: [QueryKeys.DanceSchools, DanceSchoolQueryKeys.DanceSchoolList],
    queryFn: () => {
      return GET<IDanceSchool[]>(`${BASE_URL}/my-schools`);
    },
    ...querySettings,
  });
};

export const useGetDanceSchool = (
  danceSchoolId: string,
  settings?: IQuerySettings<IDanceSchool>
) => {
  const { GET } = useHttpClient();

  const querySettings = getQuerySettings(settings);
  return useQuery<IDanceSchool, HttpClientError>({
    queryKey: [QueryKeys.DanceSchools, danceSchoolId],
    queryFn: () => {
      return GET<IDanceSchool>(`${BASE_URL}/${danceSchoolId}`);
    },
    ...querySettings,
    enabled: Boolean(danceSchoolId) && querySettings.enabled,
  });
};

export const useCreateDanceSchoolMutation = (
  settings?: IQuerySettings<IDanceSchool, ICreateDanceSchoolRequest>
) => {
  const { POST } = useHttpClient();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const createDanceSchool = async (request: ICreateDanceSchoolRequest) => {
    return POST<IDanceSchool>(`${BASE_URL}`, request);
  };

  const querySettings = getQuerySettings(settings);
  return useMutation<IDanceSchool, HttpClientError, ICreateDanceSchoolRequest>({
    ...querySettings,
    mutationFn: createDanceSchool,
    onSuccess: (response, input) => {
      showSnackbar("Successfully created dance school.");
      queryClient.invalidateQueries({
        queryKey: [
          QueryKeys.DanceSchools,
          DanceSchoolQueryKeys.DanceSchoolList,
        ],
      });
      queryClient.setQueryData([QueryKeys.DanceSchools, response.id], response);
      if (querySettings.onSuccess) {
        querySettings.onSuccess(response, input);
      }
    },
  });
};

export const useUpdateDanceSchoolPublicInfoMutation = (
  settings?: IQuerySettings<IDanceSchool, IUpdateDanceSchoolPublicInfoRequest>
) => {
  const { PUT } = useHttpClient();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const updateDanceSchoolPublicInfo = async (request: IUpdateDanceSchoolPublicInfoRequest) => {
    return PUT<IDanceSchool>(`${BASE_URL}/public-info`, request);
  };

  const querySettings = getQuerySettings(settings);
  return useMutation<IDanceSchool, HttpClientError, IUpdateDanceSchoolPublicInfoRequest>({
    ...querySettings,
    mutationFn: updateDanceSchoolPublicInfo,
    onSuccess: (response, input) => {
      showSnackbar("Successfully updated dance school.");
      queryClient.invalidateQueries({
        queryKey: [
          QueryKeys.DanceSchools,
          DanceSchoolQueryKeys.DanceSchoolList,
        ],
      });
      queryClient.setQueryData([QueryKeys.DanceSchools, response.id], response);
      if (querySettings.onSuccess) {
        querySettings.onSuccess(response, input);
      }
    },
  });
};

export const useUpdateDanceSchoolXeroSalesAccountCodeMutation = (
  settings?: IQuerySettings<IDanceSchool, IUpdateDanceSchoolXeroSalesAccountCodeRequest>
) => {
  const { PUT } = useHttpClient();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const updateDanceSchoolXeroSalesAccountCode = async (request: IUpdateDanceSchoolXeroSalesAccountCodeRequest) => {
    return PUT<IDanceSchool>(`${BASE_URL}/xero-sales-account-code`, request);
  };

  const querySettings = getQuerySettings(settings);
  return useMutation<IDanceSchool, HttpClientError, IUpdateDanceSchoolXeroSalesAccountCodeRequest>({
    ...querySettings,
    mutationFn: updateDanceSchoolXeroSalesAccountCode,
    onSuccess: (response, input) => {
      showSnackbar("Successfully updated Xero sales account code.");
      queryClient.invalidateQueries({
        queryKey: [
          QueryKeys.DanceSchools,
          DanceSchoolQueryKeys.DanceSchoolList,
        ],
      });
      queryClient.setQueryData([QueryKeys.DanceSchools, response.id], response);
      if (querySettings.onSuccess) {
        querySettings.onSuccess(response, input);
      }
    },
  });
};

export const useUpdateDanceSchoolBillingSettingsMutation = (
  settings?: IQuerySettings<IDanceSchool, IUpdateDanceSchoolBillingSettingsRequest>
) => {
  const { PUT } = useHttpClient();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const updateDanceSchoolBillingSettings = async (request: IUpdateDanceSchoolBillingSettingsRequest) => {
    return PUT<IDanceSchool>(`${BASE_URL}/billing-settings`, request);
  };

  const querySettings = getQuerySettings(settings);
  return useMutation<IDanceSchool, HttpClientError, IUpdateDanceSchoolBillingSettingsRequest>({
    ...querySettings,
    mutationFn: updateDanceSchoolBillingSettings,
    onSuccess: (response, input) => {
      showSnackbar("Successfully updated billing settings.");
      queryClient.invalidateQueries({
        queryKey: [
          QueryKeys.DanceSchools,
          DanceSchoolQueryKeys.DanceSchoolList,
        ],
      });
      queryClient.setQueryData([QueryKeys.DanceSchools, response.id], response);
      if (querySettings.onSuccess) {
        querySettings.onSuccess(response, input);
      }
    },
  });
};

export const useUpdateDanceSchoolTermsAndConditionsMutation = (
  settings?: IQuerySettings<IDanceSchool, IUpdateDanceSchoolTermsAndConditionsRequest>
) => {
  const { POST } = useHttpClient();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const updateDanceSchoolTermsAndConditions = async (request: IUpdateDanceSchoolTermsAndConditionsRequest) => {
    return POST<IDanceSchool>(`${BASE_URL}/terms-and-conditions`, request);
  };

  const querySettings = getQuerySettings(settings);
  return useMutation<IDanceSchool, HttpClientError, IUpdateDanceSchoolTermsAndConditionsRequest>({
    ...querySettings,
    mutationFn: updateDanceSchoolTermsAndConditions,
    onSuccess: (response, input) => {
      showSnackbar("Successfully updated terms and conditions.");
      queryClient.invalidateQueries({
        queryKey: [
          QueryKeys.DanceSchools,
          DanceSchoolQueryKeys.DanceSchoolList,
        ],
      });
      queryClient.setQueryData([QueryKeys.DanceSchools, response.id], response);
      if (querySettings.onSuccess) {
        querySettings.onSuccess(response, input);
      }
    },
  });
};

export const useUpsertDanceSchoolAnnualSettingsMutation = (
  settings?: IQuerySettings<IDanceSchool, IUpsertDanceSchoolAnnualSettingsRequest>
) => {
  const { PUT } = useHttpClient();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const upsertDanceSchoolAnnualSettings = async (request: IUpsertDanceSchoolAnnualSettingsRequest) => {
    return PUT<IDanceSchool>(`${BASE_URL}/annual-settings`, request);
  };

  const querySettings = getQuerySettings(settings);
  return useMutation<IDanceSchool, HttpClientError, IUpsertDanceSchoolAnnualSettingsRequest>({
    ...querySettings,
    mutationFn: upsertDanceSchoolAnnualSettings,
    onSuccess: (response, input) => {
      showSnackbar("Successfully updated dance school settings.");
      queryClient.setQueryData([QueryKeys.DanceSchools, response.id], response);
      if (querySettings.onSuccess) {
        querySettings.onSuccess(response, input);
      }
    },
  });
};

export const useStartDanceSchoolBillingYearMutation = (
  settings?: IQuerySettings<IDanceSchool, IStartDanceSchoolBillingYearRequest>
) => {
  const { POST } = useHttpClient();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const startDanceSchoolBillingYear = async (request: IStartDanceSchoolBillingYearRequest) => {
    return POST<IDanceSchool>(`${BASE_URL}/annual-settings/start-year`, request);
  };

  const querySettings = getQuerySettings(settings);
  return useMutation<IDanceSchool, HttpClientError, IStartDanceSchoolBillingYearRequest>({
    ...querySettings,
    mutationFn: startDanceSchoolBillingYear,
    onSuccess: (response, input) => {
      showSnackbar(`Successfully opened ${input.year} enrolments.`);
      queryClient.setQueryData([QueryKeys.DanceSchools, response.id], response);
      if (querySettings.onSuccess) {
        querySettings.onSuccess(response, input);
      }
    },
  });
};
